/**
 * Golf Simulation
 *
 * Provides simulation functions for golf rounds using the handicap system
 * and hole-by-hole scoring probabilities.
 */

import {
    HANDICAP_SCORING_PROFILES,
    HOLE_TYPE_ADJUSTMENTS,
    SCORE_TYPES,
    STANDARD_HOLE_LAYOUT
} from './golfConfig';

/**
 * Calculate the course handicap using the USGA formula
 * @param {number} handicapIndex - Golfer's handicap index (0-36)
 * @param {number} slopeRating - Course slope rating (55-155, 113 = average)
 * @param {number} courseRating - Course rating for scratch golfer
 * @param {number} par - Course par
 * @returns {number} Course handicap (rounded to nearest integer)
 */
export function calculateCourseHandicap(handicapIndex, slopeRating, courseRating, par) {
    const courseHandicap = handicapIndex * (slopeRating / 113) + (courseRating - par);
    return Math.round(courseHandicap);
}

/**
 * Calculate the expected score for a golfer on a course
 * @param {number} handicapIndex - Golfer's handicap index
 * @param {Object} course - Course configuration
 * @returns {number} Expected score
 */
export function calculateExpectedScore(handicapIndex, course) {
    const courseHandicap = calculateCourseHandicap(
        handicapIndex,
        course.slopeRating,
        course.courseRating,
        course.par
    );
    return course.par + courseHandicap;
}

/**
 * Get the scoring profile brackets for interpolation
 * @param {number} handicap - Handicap index
 * @returns {Object} Lower and upper bracket info
 */
function getHandicapBrackets(handicap) {
    const brackets = Object.keys(HANDICAP_SCORING_PROFILES)
        .map(Number)
        .sort((a, b) => a - b);

    // Find the brackets that surround the handicap
    let lower = brackets[0];
    let upper = brackets[brackets.length - 1];

    for (let i = 0; i < brackets.length - 1; i++) {
        if (handicap >= brackets[i] && handicap <= brackets[i + 1]) {
            lower = brackets[i];
            upper = brackets[i + 1];
            break;
        }
    }

    // Handle edge cases
    if (handicap <= brackets[0]) {
        return { lower: brackets[0], upper: brackets[0], ratio: 0 };
    }
    if (handicap >= brackets[brackets.length - 1]) {
        return { lower: brackets[brackets.length - 1], upper: brackets[brackets.length - 1], ratio: 0 };
    }

    const ratio = (handicap - lower) / (upper - lower);
    return { lower, upper, ratio };
}

/**
 * Interpolate scoring probabilities between handicap brackets
 * @param {number} handicap - Handicap index
 * @returns {Object} Interpolated scoring probabilities
 */
function interpolateScoringProfile(handicap) {
    const { lower, upper, ratio } = getHandicapBrackets(handicap);
    const lowerProfile = HANDICAP_SCORING_PROFILES[lower];
    const upperProfile = HANDICAP_SCORING_PROFILES[upper];

    const interpolated = {};
    for (const scoreType of Object.values(SCORE_TYPES)) {
        interpolated[scoreType] = lowerProfile[scoreType] * (1 - ratio) + upperProfile[scoreType] * ratio;
    }

    return interpolated;
}

/**
 * Apply hole type adjustments to scoring probabilities
 * @param {Object} baseProbabilities - Base scoring probabilities
 * @param {number} holePar - Par for the hole (3, 4, or 5)
 * @returns {Object} Adjusted probabilities
 */
function applyHoleTypeAdjustments(baseProbabilities, holePar) {
    const adjustments = HOLE_TYPE_ADJUSTMENTS[holePar] || HOLE_TYPE_ADJUSTMENTS[4];
    const adjusted = {};

    for (const scoreType of Object.values(SCORE_TYPES)) {
        adjusted[scoreType] = Math.max(0, baseProbabilities[scoreType] + adjustments[scoreType]);
    }

    // Normalise to ensure probabilities sum to 1
    const sum = Object.values(adjusted).reduce((a, b) => a + b, 0);
    for (const scoreType of Object.values(SCORE_TYPES)) {
        adjusted[scoreType] = adjusted[scoreType] / sum;
    }

    return adjusted;
}

/**
 * Apply consistency modifier to probabilities
 * Higher consistency = more pars, fewer extremes
 * Lower consistency = more variance in scores
 * @param {Object} baseProbabilities - Base scoring probabilities
 * @param {number} consistency - Consistency factor (0.15 = very consistent, 0.35 = erratic)
 * @returns {Object} Adjusted probabilities
 */
function applyConsistencyModifier(baseProbabilities, consistency) {
    // Baseline consistency is 0.25
    const baseline = 0.25;
    const consistencyFactor = consistency / baseline;

    // Calculate the "extreme" factor - how much to shift towards/away from par
    // consistencyFactor < 1 means more consistent (shift towards par)
    // consistencyFactor > 1 means less consistent (shift towards extremes)

    const adjusted = { ...baseProbabilities };

    // Get current par probability
    const parProb = adjusted[SCORE_TYPES.PAR];

    // Calculate how much to shift
    // More consistent: increase par, decrease extremes
    // Less consistent: decrease par, increase extremes
    const shiftAmount = (1 - consistencyFactor) * 0.15;  // Max 15% shift

    // Shift par probability
    adjusted[SCORE_TYPES.PAR] = Math.max(0.05, Math.min(0.90, parProb + shiftAmount));

    // Redistribute the difference to other score types proportionally
    const parDiff = adjusted[SCORE_TYPES.PAR] - parProb;
    const otherTypes = Object.values(SCORE_TYPES).filter(t => t !== SCORE_TYPES.PAR);
    const otherSum = otherTypes.reduce((sum, t) => sum + baseProbabilities[t], 0);

    for (const scoreType of otherTypes) {
        const proportion = baseProbabilities[scoreType] / otherSum;
        adjusted[scoreType] = Math.max(0, baseProbabilities[scoreType] - parDiff * proportion);
    }

    // Normalise
    const sum = Object.values(adjusted).reduce((a, b) => a + b, 0);
    for (const scoreType of Object.values(SCORE_TYPES)) {
        adjusted[scoreType] = adjusted[scoreType] / sum;
    }

    return adjusted;
}

/**
 * Get scoring probabilities for a specific hole
 * @param {number} handicapIndex - Golfer's handicap index
 * @param {number} holePar - Par for the hole
 * @param {number} consistency - Consistency factor (default 0.25)
 * @returns {Object} Scoring probabilities for each score type
 */
export function getScoringProbabilities(handicapIndex, holePar, consistency = 0.25) {
    // Get interpolated base probabilities for this handicap
    let probabilities = interpolateScoringProfile(handicapIndex);

    // Apply hole type adjustments
    probabilities = applyHoleTypeAdjustments(probabilities, holePar);

    // Apply consistency modifier
    probabilities = applyConsistencyModifier(probabilities, consistency);

    return probabilities;
}

/**
 * Convert a score type to actual strokes relative to par
 * @param {string} scoreType - Score type from SCORE_TYPES
 * @returns {number} Strokes relative to par
 */
function scoreTypeToStrokes(scoreType) {
    switch (scoreType) {
        case SCORE_TYPES.EAGLE_OR_BETTER:
            return -2;  // Could be eagle or better, use -2 as average
        case SCORE_TYPES.BIRDIE:
            return -1;
        case SCORE_TYPES.PAR:
            return 0;
        case SCORE_TYPES.BOGEY:
            return 1;
        case SCORE_TYPES.DOUBLE_BOGEY:
            return 2;
        case SCORE_TYPES.TRIPLE_OR_WORSE:
            return 3;  // Could be worse, use +3 as average
        default:
            return 0;
    }
}

/**
 * Get result label from strokes relative to par
 * @param {number} strokesRelativeToPar - Score relative to par
 * @returns {string} Result label
 */
function getResultLabel(strokesRelativeToPar) {
    if (strokesRelativeToPar <= -2) return 'eagle';
    if (strokesRelativeToPar === -1) return 'birdie';
    if (strokesRelativeToPar === 0) return 'par';
    if (strokesRelativeToPar === 1) return 'bogey';
    if (strokesRelativeToPar === 2) return 'double';
    return 'triple+';
}

/**
 * Simulate a single hole
 * @param {number} handicapIndex - Golfer's handicap index
 * @param {Object} hole - Hole configuration { number, par, strokeIndex }
 * @param {number} consistency - Consistency factor
 * @returns {Object} Hole result
 */
export function simulateHole(handicapIndex, hole, consistency = 0.25) {
    const probabilities = getScoringProbabilities(handicapIndex, hole.par, consistency);

    // Generate random number and select score type based on cumulative probabilities
    const rand = Math.random();
    let cumulative = 0;
    let selectedScoreType = SCORE_TYPES.PAR;

    for (const [scoreType, probability] of Object.entries(probabilities)) {
        cumulative += probability;
        if (rand < cumulative) {
            selectedScoreType = scoreType;
            break;
        }
    }

    const strokesRelativeToPar = scoreTypeToStrokes(selectedScoreType);
    const score = hole.par + strokesRelativeToPar;

    return {
        hole: hole.number,
        par: hole.par,
        strokeIndex: hole.strokeIndex,
        score,
        relativeToPar: strokesRelativeToPar,
        result: getResultLabel(strokesRelativeToPar),
        scoreType: selectedScoreType
    };
}

/**
 * Simulate a complete round of golf
 * @param {number} handicapIndex - Golfer's handicap index
 * @param {Object} course - Course configuration
 * @param {number} consistency - Consistency factor
 * @returns {Object} Round result
 */
export function simulateRound(handicapIndex, course, consistency = 0.25) {
    const holes = course.holes || STANDARD_HOLE_LAYOUT;
    const holeResults = holes.map(hole => simulateHole(handicapIndex, hole, consistency));

    // Calculate totals
    const totalScore = holeResults.reduce((sum, h) => sum + h.score, 0);
    const frontNine = holeResults.slice(0, 9).reduce((sum, h) => sum + h.score, 0);
    const backNine = holeResults.slice(9, 18).reduce((sum, h) => sum + h.score, 0);

    // Count score types
    const scoreCounts = {
        eagles: 0,
        birdies: 0,
        pars: 0,
        bogeys: 0,
        doubleBogeys: 0,
        tripleOrWorse: 0
    };

    holeResults.forEach(h => {
        switch (h.scoreType) {
            case SCORE_TYPES.EAGLE_OR_BETTER:
                scoreCounts.eagles++;
                break;
            case SCORE_TYPES.BIRDIE:
                scoreCounts.birdies++;
                break;
            case SCORE_TYPES.PAR:
                scoreCounts.pars++;
                break;
            case SCORE_TYPES.BOGEY:
                scoreCounts.bogeys++;
                break;
            case SCORE_TYPES.DOUBLE_BOGEY:
                scoreCounts.doubleBogeys++;
                break;
            case SCORE_TYPES.TRIPLE_OR_WORSE:
                scoreCounts.tripleOrWorse++;
                break;
            default:
                // Unknown score type, no action needed
                break;
        }
    });

    // Calculate par type averages
    const parThreeHoles = holeResults.filter(h => h.par === 3);
    const parFourHoles = holeResults.filter(h => h.par === 4);
    const parFiveHoles = holeResults.filter(h => h.par === 5);

    const parThreeAvg = parThreeHoles.length > 0
        ? parThreeHoles.reduce((sum, h) => sum + h.score, 0) / parThreeHoles.length
        : 0;
    const parFourAvg = parFourHoles.length > 0
        ? parFourHoles.reduce((sum, h) => sum + h.score, 0) / parFourHoles.length
        : 0;
    const parFiveAvg = parFiveHoles.length > 0
        ? parFiveHoles.reduce((sum, h) => sum + h.score, 0) / parFiveHoles.length
        : 0;

    const courseHandicap = calculateCourseHandicap(
        handicapIndex,
        course.slopeRating,
        course.courseRating,
        course.par
    );

    return {
        totalScore,
        relativeToPar: totalScore - course.par,
        frontNine,
        backNine,
        ...scoreCounts,
        holes: holeResults,
        parThreeAvg: Math.round(parThreeAvg * 10) / 10,
        parFourAvg: Math.round(parFourAvg * 10) / 10,
        parFiveAvg: Math.round(parFiveAvg * 10) / 10,
        golferHandicap: handicapIndex,
        courseHandicap,
        expectedScore: course.par + courseHandicap
    };
}

/**
 * Simulate multiple rounds of golf
 * @param {number} handicapIndex - Golfer's handicap index
 * @param {Object} course - Course configuration
 * @param {number} count - Number of rounds to simulate
 * @param {number} consistency - Consistency factor
 * @returns {Object} Aggregated results
 */
export function simulateMultipleRounds(handicapIndex, course, count = 500, consistency = 0.25) {
    const rounds = [];
    const scores = [];

    for (let i = 0; i < count; i++) {
        const round = simulateRound(handicapIndex, course, consistency);
        rounds.push(round);
        scores.push(round.totalScore);
    }

    // Calculate statistics
    const avgScore = scores.reduce((a, b) => a + b, 0) / count;
    const sortedScores = [...scores].sort((a, b) => a - b);
    const best10Percentile = sortedScores[Math.floor(count * 0.1)];
    const worst10Percentile = sortedScores[Math.floor(count * 0.9)];
    const median = sortedScores[Math.floor(count / 2)];

    // Group scores for distribution
    const scoreCounts = {};
    scores.forEach(score => {
        scoreCounts[score] = (scoreCounts[score] || 0) + 1;
    });

    const scoreDistribution = Object.entries(scoreCounts)
        .map(([score, count]) => ({
            score: parseInt(score),
            count,
            percentage: (count / rounds.length) * 100
        }))
        .sort((a, b) => a.score - b.score);

    // Fill in gaps
    if (scoreDistribution.length > 0) {
        const minScore = scoreDistribution[0].score;
        const maxScore = scoreDistribution[scoreDistribution.length - 1].score;
        const filledDistribution = [];

        for (let s = minScore; s <= maxScore; s++) {
            const existing = scoreDistribution.find(d => d.score === s);
            filledDistribution.push(existing || { score: s, count: 0, percentage: 0 });
        }

        return {
            rounds,
            scores,
            avgScore: Math.round(avgScore * 10) / 10,
            median,
            best10Percentile,
            worst10Percentile,
            minScore: Math.min(...scores),
            maxScore: Math.max(...scores),
            scoreDistribution: filledDistribution,
            expectedScore: course.par + calculateCourseHandicap(
                handicapIndex,
                course.slopeRating,
                course.courseRating,
                course.par
            )
        };
    }

    return {
        rounds,
        scores,
        avgScore: Math.round(avgScore * 10) / 10,
        median,
        best10Percentile,
        worst10Percentile,
        minScore: Math.min(...scores),
        maxScore: Math.max(...scores),
        scoreDistribution,
        expectedScore: course.par + calculateCourseHandicap(
            handicapIndex,
            course.slopeRating,
            course.courseRating,
            course.par
        )
    };
}

/**
 * Get hole-by-hole probability matrix for visualisation
 * @param {number} handicapIndex - Golfer's handicap index
 * @param {Object} course - Course configuration
 * @param {number} consistency - Consistency factor
 * @returns {Array} Array of probability objects per hole
 */
export function getHoleProbabilityMatrix(handicapIndex, course, consistency = 0.25) {
    const holes = course.holes || STANDARD_HOLE_LAYOUT;

    return holes.map(hole => ({
        hole: hole.number,
        par: hole.par,
        strokeIndex: hole.strokeIndex,
        probabilities: getScoringProbabilities(handicapIndex, hole.par, consistency)
    }));
}
